import { Box, Link, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { AcademyLesson } from '../../../pages/academy/Academy/AcademyService';
import {
  learningContentDetailsClassNames,
  learningContentDetailsStyles,
} from './LearningContentDetails.styles';

export interface LearningContentDetailsProps {
  skill: AcademyLesson;
}

export function LearningContentDetails({ skill }: LearningContentDetailsProps) {
  const theme = useTheme();
  const {
    referenceText = [],
    goals = [],
    practice = [],
    sections = [],
    resources = [],
    transcript = [],
    timingNote,
    supportNote,
    checkPrompt,
  } = skill.learnContent.details;
  const hasSupportContent =
    referenceText.length > 0 ||
    goals.length > 0 ||
    practice.length > 0 ||
    sections.length > 0 ||
    resources.length > 0 ||
    Boolean(timingNote) ||
    Boolean(supportNote) ||
    Boolean(checkPrompt) ||
    transcript.length > 0;

  if (!hasSupportContent) {
    return null;
  }

  const companionResourcesHeadingId = `${skill.skillKey}-companion-resources`;

  return (
    <Box
      className={learningContentDetailsClassNames.root}
      sx={learningContentDetailsStyles.root()}
    >
      {goals.length > 0 || practice.length > 0 ? (
        <Box
          className={learningContentDetailsClassNames.columns}
          sx={learningContentDetailsStyles.columns(theme)}
        >
          {goals.length > 0 ? (
            <Box component="section" aria-labelledby={`${skill.skillKey}-learn-goals`}>
              <Typography id={`${skill.skillKey}-learn-goals`} component="h4">
                Covered in this skill
              </Typography>
              <Box component="ul">
                {goals.map((goal, index) => (
                  <li key={index}>{goal}</li>
                ))}
              </Box>
            </Box>
          ) : null}

          {practice.length > 0 ? (
            <Box component="section" aria-labelledby={`${skill.skillKey}-learn-practice`}>
              <Typography id={`${skill.skillKey}-learn-practice`} component="h4">
                Try in Exploros
              </Typography>
              <Box component="ol">
                {practice.map((practiceStep, index) => (
                  <li key={index}>{practiceStep}</li>
                ))}
              </Box>
            </Box>
          ) : null}
        </Box>
      ) : null}

      {sections.length > 0 ? (
        <Box
          className={learningContentDetailsClassNames.sectionList}
          sx={learningContentDetailsStyles.sectionList()}
        >
          {sections.map((section, index) => {
            const headingId = `${skill.skillKey}-${slugLabel(section.title)}-${index}`;

            return (
              <Box component="section" key={headingId} aria-labelledby={headingId}>
                <Typography id={headingId} component="h4">
                  {section.title}
                </Typography>
                {section.body.map((paragraph, paragraphIndex) => (
                  <Typography key={paragraphIndex}>{paragraph}</Typography>
                ))}
              </Box>
            );
          })}
        </Box>
      ) : null}

      {referenceText.length > 0 || resources.length > 0 ? (
        <Box
          component="section"
          className={learningContentDetailsClassNames.resources}
          sx={learningContentDetailsStyles.resources()}
          aria-label={resources.length > 0 ? undefined : 'Referenced resources'}
          aria-labelledby={resources.length > 0 ? companionResourcesHeadingId : undefined}
        >
          {resources.length > 0 ? (
            <Typography
              id={companionResourcesHeadingId}
              component="h4"
              sx={learningContentDetailsStyles.resourcesHeading()}
            >
              Companion Resources
            </Typography>
          ) : null}
          {referenceText.map((paragraph, index) => (
            <Typography key={index}>{paragraph}</Typography>
          ))}
          {resources.length > 0 ? (
            <Box component="ul" sx={learningContentDetailsStyles.resourceList()}>
              {resources.map((resource, index) => (
                <li key={index}>
                  <Link href={resource.url} target="_blank" rel="noreferrer" underline="none">
                    {index === 0 ? `See ${resource.label}` : resource.label}
                  </Link>
                </li>
              ))}
            </Box>
          ) : null}
        </Box>
      ) : null}

      {timingNote ? (
        <Typography
          className={learningContentDetailsClassNames.note}
          sx={learningContentDetailsStyles.note()}
        >
          {timingNote}
        </Typography>
      ) : null}
      {supportNote ? (
        <Typography
          className={learningContentDetailsClassNames.note}
          sx={learningContentDetailsStyles.note()}
        >
          {supportNote}
        </Typography>
      ) : null}
      {checkPrompt ? (
        <Typography
          className={learningContentDetailsClassNames.check}
          sx={learningContentDetailsStyles.check()}
        >
          {checkPrompt}
        </Typography>
      ) : null}

      {transcript.length > 0 ? (
        <Box
          component="details"
          className={learningContentDetailsClassNames.transcript}
          sx={learningContentDetailsStyles.transcript()}
        >
          <summary>Video script</summary>
          <Box>
            {transcript.map((paragraph, index) => (
              <Typography key={index}>{paragraph}</Typography>
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

function slugLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}
